/**
 * Public, unauthenticated privacy policy for the Atlas Copilot browser
 * extension. Linked from the Chrome Web Store listing. Must remain
 * publicly accessible (no auth, no app chrome) so reviewers can load
 * it directly.
 */
export default function PrivacyPolicyPage() {
  return (
    <main className="mx-auto max-w-3xl px-6 py-12 text-slate-800">
      <header className="mb-8 border-b border-slate-200 pb-6">
        <h1 className="text-3xl font-semibold text-[#5b1d5b]">
          Atlas Copilot Privacy Policy
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          Last updated: May 27, 2026
        </p>
      </header>

      <Section title="Scope">
        <p>
          This Privacy Policy describes how the Atlas Copilot Chrome
          browser extension and the Atlas Copilot web application
          (together, &ldquo;Atlas Copilot&rdquo;) handle information.
          Atlas Copilot is a private clinical workflow tool operated by
          Astrana Health, Inc. (&ldquo;Astrana Health,&rdquo;
          &ldquo;we,&rdquo; or &ldquo;us&rdquo;). It supplements
          Astrana Health&rsquo;s general Privacy Policy at{" "}
          <a
            className="text-[#5b1d5b] underline"
            href="https://www.astranahealth.com/privacy-policy/"
          >
            astranahealth.com/privacy-policy
          </a>
          ; where the two are inconsistent with respect to
          Atlas Copilot, this policy controls.
        </p>
      </Section>

      <Section title="Who can use Atlas Copilot">
        <p>
          Atlas Copilot is distributed privately to clinicians and care
          team users at clinical organizations that have an existing
          Business Associate Agreement (&ldquo;BAA&rdquo;) with Astrana
          Health. It is not available to the general public, and is
          not intended for use by patients or consumers.
        </p>
      </Section>

      <Section title="What Atlas Copilot accesses">
        <h3 className="mt-4 mb-1 font-semibold">On the user&rsquo;s device</h3>
        <p>
          When the user opens an Elation EMR tab (
          <code>*.elationemr.com</code>, <code>*.elationhealth.com</code>),
          Atlas Copilot reads the currently-open patient identifier and,
          when present, fields from the open SOAP note (subjective,
          objective, assessment, plan, vitals, problem list, follow-up,
          visit metadata). This information is used solely to keep the
          Atlas Copilot side panel synchronized with the patient the
          clinician is viewing.
        </p>

        <h3 className="mt-4 mb-1 font-semibold">From Astrana</h3>
        <p>
          When the user signs in with their Astrana-managed Microsoft
          account, Atlas Copilot retrieves clinical and administrative
          information about the matching member from Astrana&rsquo;s
          care-management services, including: demographics,
          eligibility, authorizations, care team, hierarchical
          condition category (HCC) suspect conditions, lab results,
          and recent claims.
        </p>

        <h3 className="mt-4 mb-1 font-semibold">Account information</h3>
        <p>
          Atlas Copilot identifies the signed-in user by their
          Microsoft Azure Active Directory account (typically: email
          address, display name, and Azure AD object identifier).
        </p>
      </Section>

      <Section title="What Atlas Copilot stores locally">
        <p>
          Atlas Copilot stores in <code>chrome.storage</code> only: the
          user&rsquo;s OAuth2 access and refresh tokens for
          Astrana&rsquo;s Microsoft Azure AD application, and
          lightweight non-clinical UI preferences. Atlas Copilot does
          not persist patient clinical data to local storage.
        </p>
      </Section>

      <Section title="Where data is transmitted">
        <p>
          All clinical data displayed by Atlas Copilot flows directly
          between the user&rsquo;s browser and Astrana Health&rsquo;s
          HIPAA-compliant backend services. Authentication tokens are
          exchanged with Microsoft&rsquo;s identity platform at{" "}
          <code>login.microsoftonline.com</code>. Atlas Copilot does
          not transmit data to any other third party, does not include
          third-party analytics or advertising software, and does not
          sell or share user or patient data for any purpose.
        </p>
      </Section>

      <Section title="HIPAA, BAA, and lawful basis">
        <p>
          Use of Atlas Copilot with Protected Health Information
          (&ldquo;PHI&rdquo;) is governed by the Business Associate
          Agreement between Astrana Health and the user&rsquo;s
          employing clinical organization. Workforce members of those
          organizations access PHI through Atlas Copilot for treatment,
          payment, and health care operations as permitted by HIPAA
          and that BAA.
        </p>
      </Section>

      <Section title="Data retention and deletion">
        <p>
          Locally cached tokens and preferences are cleared when the
          user signs out of Atlas Copilot or uninstalls the extension.
          Server-side data is retained under Astrana Health&rsquo;s
          standard records-retention schedule as governed by the
          applicable BAA.
        </p>
      </Section>

      <Section title="Children">
        <p>
          Atlas Copilot is not directed to consumers and is not
          intended to be used directly by individuals under the age of
          18. Pediatric clinical information may be present in a
          member&rsquo;s record where the member is a pediatric
          patient of an Astrana Health clinical partner; such
          information is handled under the same HIPAA and BAA terms
          as all other PHI.
        </p>
      </Section>

      <Section title="Changes to this policy">
        <p>
          We may update this policy from time to time. Material
          changes will be reflected by an updated &ldquo;Last
          updated&rdquo; date at the top of this page. Continued use
          of Atlas Copilot after such an update constitutes acceptance
          of the revised policy.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          Questions about this policy, or requests related to data
          accessed by Atlas Copilot, may be directed to{" "}
          <a
            className="text-[#5b1d5b] underline"
            href="mailto:privacy@astranahealth.com"
          >
            privacy@astranahealth.com
          </a>
          .
        </p>
      </Section>
    </main>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="mb-3 text-xl font-semibold text-[#5b1d5b]">{title}</h2>
      <div className="space-y-2 leading-relaxed">{children}</div>
    </section>
  );
}
